import moment from "moment";
import React from "react";
import { Modal } from "../components";
import { NoDataAvailableDiv } from "../components/NoDataAvailableDiv";
import Panel from "../components/panels/panel";
import EditReminder from "./EditReminder";
import { ButtonSecondary } from "components/Atoms/Button";
import { Reminder } from "./models";
import { useMutation } from "@tanstack/react-query";

interface Props {
  maxDaysAfterSendDate: number
  documentSendMoment: moment.Moment

  reminders: Reminder[];

  canClearAll: boolean
  canDisplayAddNewReminder: boolean

  refetch: () => Promise<any>;

  renderCard: (
    reminder: Reminder,
    onEdit: (reminder: Reminder) => void,
    clearLastReminder: () => Promise<void>,
    isLast: boolean
  ) => React.ReactNode

  clearLastReminder: () => Promise<any>
  deleteAllReminders: () => Promise<any>
  addReminder: (days: number) => Promise<any>
  updateReminder: (days: number, reminderNr: number) => Promise<any>
}

const DocumentReminderPanel = (props: React.PropsWithChildren<Props>) => {
  const [editingReminder, setEditingReminder] = React.useState<Reminder>(undefined);

  const clearLastReminderMutation = useMutation({
    mutationFn: props.clearLastReminder,
    onSuccess: () => props.refetch()
  });

  const clearLastReminder = async () => {
    if (clearLastReminderMutation.isLoading) return
    await clearLastReminderMutation.mutateAsync()
  }

  const deleteAllRemindersMutation = useMutation({
    mutationFn: props.deleteAllReminders,
    onSuccess: () => props.refetch()
  });
  const deleteAllReminders = async () => {
    if (deleteAllRemindersMutation.isLoading) return
    await deleteAllRemindersMutation.mutateAsync()
  }

  const addReminderMutation = useMutation({
    mutationFn: props.addReminder,
    onSuccess: () => props.refetch()
  });

  const addReminder = async (days: number) => {
    if (addReminderMutation.isLoading) return;
    await addReminderMutation.mutateAsync(days);
  };

  const updateReminderMutation = useMutation({
    mutationFn: ({ days, reminderNr }: { days: number, reminderNr: number }) => props.updateReminder(days, reminderNr),
    onSuccess: () => props.refetch()
  });

  const updateReminder = async (days: number, reminderNr: number) => {
    if (updateReminderMutation.isLoading) return;
    await updateReminderMutation.mutateAsync({ days, reminderNr });
  };


  return <Panel
    panelClassName="s1_related-feature-panel"
    title={RESX.Reminder.Reminders}
    articleId={serverReference.supportArticleIds.whatAreReminders}
    button={
      props.canDisplayAddNewReminder && <ButtonSecondary
        onClick={() => { setEditingReminder(null); }}
        label={RESX.Buttons.Add}
      />
    }
  >

    {props.reminders.length === 0 && <NoDataAvailableDiv />}

    {props.reminders.length > 0 && <div className="gap" style={{ display: "grid", gridTemplateColumns: "auto 1fr auto" }}>
      {props.reminders.map((reminder, index) => (
        props.renderCard(
          reminder,
          setEditingReminder,
          clearLastReminder,
          index === props.reminders.length - 1
        )
      ))}
    </div>}

    {props.canClearAll && <div className="flex justify-center margin-top">
      <a onClick={deleteAllReminders}>{RESX.Buttons.btnClearAll}</a>
    </div>}

    <Modal
      isOpen={editingReminder !== undefined}
      handleClose={() => { setEditingReminder(undefined); }}
      title={editingReminder == null ? "Add reminder" : RESX.Reminder.xReminder.replace("{0}", editingReminder.reminderNr.toString())}
      size="small"
    >
      <EditReminder
        reminders={props.reminders}
        documentSendMoment={props.documentSendMoment}

        maxDaysAfterSendDate={props.maxDaysAfterSendDate}
        existingReminder={editingReminder}
        addReminder={async (days) => {
          await addReminder(days);
          setEditingReminder(undefined);
        }}
        updateReminder={async (days, reminderNr) => {
          await updateReminder(days, reminderNr);
          setEditingReminder(undefined);
        }}
      />
    </Modal>
  </Panel>;
};

export default DocumentReminderPanel;